import React from 'react';
import clsx from 'clsx';
import styles from './Input.module.css';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  ref?: React.Ref<HTMLInputElement>;
  label?: string | React.ReactNode;
  error?: string | React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  wrapperClassName?: string;
  dataTestId?: string;
}

function Input({
  ref,
  label,
  error,
  leftIcon,
  rightIcon,
  required,
  className,
  wrapperClassName,
  id,
  dataTestId,
  ...props
}: InputProps) {
  // Generate ID for label association if not provided
  const generatedId = React.useId();
  const inputId = id || `input-${generatedId}`;

  return (
    <div className={clsx(styles.inputWrapper, wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className={styles.label} data-test-id={dataTestId ? `${dataTestId}-label` : undefined}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputContainer}>
        {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}

        <input
          id={inputId}
          className={clsx(
            styles.input,
            leftIcon && styles.inputWithLeftIcon,
            rightIcon && styles.inputWithRightIcon,
            error && styles.inputError,
            className
          )}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
          data-test-id={dataTestId ? `${dataTestId}-input` : undefined}
          {...props}
          ref={ref}
        />

        {rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
      </div>

      {error && (
        <div
          id={`${inputId}-error`}
          className={styles.errorMessage}
          role="alert"
          data-test-id={dataTestId ? `${dataTestId}-error` : 'validation-error'}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default Input;
